import styled, { keyframes } from "styled-components";
import { Row, Col, Card } from "react-bootstrap";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const WorkExpCard = styled(Card)`
  background: rgba(11, 1, 1, 0.2);
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.37);
  backdrop-filter: blur(19px);
  -webkit-backdrop-filter: blur(44px);
  border-color: transparent;
  border-radius: 16px;
  height: auto;
  justify-content: center;
  padding-bottom: 40px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #ff003c, #fea794, #ff003c);
    background-size: 200% 100%;
  }
`;

const TimelineItem = styled.div`
  position: relative;
  padding-left: 30px;
  margin-bottom: 24px;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 8px;
    width: 12px;
    height: 12px;
    background: linear-gradient(135deg, #ff003c, #fea794);
    border-radius: 50%;
    box-shadow: 0 0 10px rgba(255, 0, 60, 0.5);
  }

  &::after {
    content: "";
    position: absolute;
    left: 5px;
    top: 24px;
    width: 2px;
    height: calc(100% + 12px);
    background: linear-gradient(180deg, rgba(255, 0, 60, 0.5), transparent);
  }

  &:last-child::after {
    display: none;
  }
`;

const YearBadge = styled.span`
  display: inline-block;
  background: linear-gradient(
    135deg,
    rgba(255, 0, 60, 0.2),
    rgba(254, 167, 148, 0.1)
  );
  border: 1px solid rgba(255, 0, 60, 0.3);
  color: #fea794;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
  margin-bottom: 8px;
  letter-spacing: 1px;
`;

const CurrentBadge = styled(YearBadge)`
  background: linear-gradient(
    135deg,
    rgba(0, 255, 136, 0.15),
    rgba(0, 255, 136, 0.05)
  );
  border: 1px solid rgba(0, 255, 136, 0.4);
  color: #00ff88;

  &::before {
    content: "";
    display: inline-block;
    width: 6px;
    height: 6px;
    background: #00ff88;
    border-radius: 50%;
    margin-right: 6px;
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.4;
    }
  }
`;

const JobTitle = styled.h5`
  background: linear-gradient(135deg, #ff003c, #fea794);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 4px;
  letter-spacing: 0.5px;
`;

const CompanyName = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;

  &::before {
    content: "";
    width: 14px;
    height: 14px;
    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23fea794'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4'/%3E%3C/svg%3E")
      no-repeat center;
    background-size: contain;
    opacity: 0.8;
  }
`;

const JobDescription = styled.ul`
  color: rgba(255, 255, 255, 0.55);
  font-size: 12px;
  line-height: 1.8;
  margin: 0;
  padding-left: 16px;

  li {
    margin-bottom: 4px;

    &::marker {
      color: #ff003c;
    }
  }
`;

const SectionTitle = styled.h3`
  background: linear-gradient(135deg, #ff003c, #fea794);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 24px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 12px;

  &::before {
    content: "";
    width: 4px;
    height: 28px;
    background: linear-gradient(180deg, #ff003c, #fea794);
    border-radius: 2px;
  }
`;

const StyledLine = styled.hr`
  background: linear-gradient(
    to right,
    transparent,
    rgba(255, 0, 60, 0.3),
    transparent
  );
  height: 1px;
  border: 0;
  margin: 16px 0 24px 0;
`;

function WorkExp() {
  const experiences = [
    {
      year: "2026-Present",
      isCurrent: true,
      title: "Deputy Manager",
      subtitle: "Digital Technology Department",
      company: "The Federation of Thai Industries",
      responsibilities: [
        "Design, develop, and maintain high-quality applications and digital services",
        "Oversee cybersecurity practices to ensure system and data security",
        "Lead and support the digital technology team with technical guidance",
        "Collaborate with cross-functional teams to translate business requirements into solutions",
        "Monitor system performance and improve digital platforms for efficiency",
      ],
    },
    {
      year: "2023-2025",
      isCurrent: false,
      title: "Back End Developer",
      company: "Gunkul Engineering PCL",
      responsibilities: [
        "Develop and improve in-house energy monitoring platform",
        "Develop new features based on requirements",
        "Collaborate with PM and BU team to analyze and support live incidents",
        "Troubleshoot and fix defects",
      ],
    },
    {
      year: "2022-2023",
      isCurrent: false,
      title: "Back End Developer (+DevOps)",
      company: "True Voice Co., Ltd",
      responsibilities: [
        "Improve Voicebot and Chatbot conversation flow on in-house platform",
        "Develop and maintain unit testing",
        "Troubleshoot and fix defects",
        "Analyze and support live incidents from customers",
      ],
    },
    {
      year: "2019-2021",
      isCurrent: false,
      title: "Full-Stack Developer",
      company: "The Federation of Thai Industries",
      responsibilities: [
        "Collaborate with management and customers to identify requirements",
        "Produce efficient and elegant code based on specifications",
        "Observe user feedback to recommend improvements",
      ],
    },
    {
      year: "2017-2019",
      isCurrent: false,
      title: "Tech Administrator",
      company: "GS1 Thailand",
      responsibilities: [
        "Maintain Computer Systems and complete system installs/upgrades",
        "Oversee Data Storage and Retrieval Systems",
        "Ensure security and efficiency of IT infrastructure",
        "Design and deliver web infrastructure",
      ],
    },
  ];

  return (
    <>
      <Col md={12} className="mt-3">
        <WorkExpCard className="shadow-sm">
          <Card.Title className="pt-4 px-4">
            <SectionTitle>Career Journey</SectionTitle>
            <StyledLine />
          </Card.Title>
          <Row className="px-4">
            <Col md={12}>
              {experiences.map((exp, index) => (
                <TimelineItem key={index}>
                  <Row>
                    <Col md={3}>
                      {exp.isCurrent ? (
                        <CurrentBadge>{exp.year}</CurrentBadge>
                      ) : (
                        <YearBadge>{exp.year}</YearBadge>
                      )}
                    </Col>
                    <Col md={4}>
                      <JobTitle>
                        {exp.title}
                        {exp.subtitle && (
                          <>
                            <br />
                            <span style={{ fontSize: "14px", fontWeight: 500 }}>
                              {exp.subtitle}
                            </span>
                          </>
                        )}
                      </JobTitle>
                      <CompanyName>{exp.company}</CompanyName>
                    </Col>
                    <Col md={5}>
                      <JobDescription>
                        {exp.responsibilities.map((resp, idx) => (
                          <li key={idx}>{resp}</li>
                        ))}
                      </JobDescription>
                    </Col>
                  </Row>
                </TimelineItem>
              ))}
            </Col>
          </Row>
        </WorkExpCard>
      </Col>
    </>
  );
}

export default WorkExp;
